// NOTE: This component is based on LetsGetYouBookedMacroComponent.tsx
// Intentional differences include:
// - Integration of dynamic payment link generation and display.
// - Payment terms changed to 4 weeks prior to arrival (instead of 6).
// - Formatting change: Added underline from "InMotion Health and Fitness." in Wise instructions.
// - Removed closing line: "*Please select fast pay with card*".
import './LetsGetYouBookedMacroPaymentLinkComponent.tsx.css';

import '../../../../js/loadSyncfusion';

import { HtmlEditor, Image, Inject, Link, QuickToolbar, RichTextEditorComponent, Toolbar } from '@syncfusion/ej2-react-richtexteditor';

import { isReservationExistingPerson, isReservationNewPerson, multiplyAndRemoveDecimal, PersonDto, ReservationPerson, ReservationPosItem, ReservationState, getDisplayName } from "@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model"; // Added ReservationPosItem
import { getProgramTypeDisplayName, getRoomTypeDisplayName } from "@digitalopsdev/serviceops-reservations-components/dist/EditReservation/OrderDetailsComponent";
import { addWeeks, differenceInHours, format, isBefore, startOfDay } from 'date-fns';
import { isReservationQuoteStatus, ReservationQuoteStatus } from '../ReservationStatus';
import { ReactNode, useState, useEffect, useRef, useCallback, KeyboardEvent } from 'react'; // Added KeyboardEvent
// Removed multiplyAndRemoveDecimal from this import as it comes from ../model
import { countProgramDaysBetween, longToMoney } from '../../../../js/utils';
import React from 'react';
import { useModelState } from '../../../../js/useModelState';
import { MacroCopyButtons } from './MacroCopyButtons';
import { getFullPaymentFeePercent, getPartialPaymentFeePercent } from './paymentFeeRates';

// Define enum locally
enum PaymentLinkType {
    Full = 0,
    Partial = 1,
}

// Define API response structure locally
interface PaymentLinkResultItem {
    paymentType: PaymentLinkType;
    url: string | null;
    error: string | null;
}

interface GeneratePaymentLinksResponse {
    paymentLinkResults: PaymentLinkResultItem[];
}

// --- Renamed Component and Props ---
export function LetsGetYouBookedMacroPaymentLinkComponent({ reservationState }: LetsGetYouBookedMacroPaymentLinkComponentProps) {
    useModelState(reservationState.Person);

    // Status: loading -> generatingLinks -> (generatingPartialLink) -> ready | error
    const [status, setStatus] = useState<'loading' | 'generatingLinks' | 'generatingPartialLink' | 'error' | 'ready'>('loading');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [firstName, setFirstName] = useState<string>('');
    const [richTextContent, setRichTextContent] = useState<ReactNode | null>(null);
    const [rteKey, setRteKey] = useState(0); // Key to force RTE remount

    // State for links, deposit, provider, and cache
    const [fullPaymentLink, setFullPaymentLink] = useState<string | null>(null);
    const [partialPaymentLink, setPartialPaymentLink] = useState<string | null>(null);
    const [depositPercent, setDepositPercent] = useState<number>(50); // Actual percentage value used in logic
    const [depositPercentInput, setDepositPercentInput] = useState<string>("50"); // Value in the input box
    const [isDepositPercentValid, setIsDepositPercentValid] = useState<boolean>(true);
    const [provider, setProvider] = useState<'Omise' | 'Stripe' | '2C2P'>('Stripe');
    const [partialLinkCache, setPartialLinkCache] = useState<Record<string, Record<number, string | null>>>({ Omise: {}, Stripe: {}, '2C2P': {} }); // Cache for partial links per provider

    // Ref to store calculated values
    const bookingDataRef = useRef<{
        totalPrice: number; // Stored as integer (cents/satang)
        totalPriceText: string; // Formatted string
        earliestDate: Date | null;
        programsEle: ReactNode;
        roomsEle: ReactNode;
        reservationNumber: number | null;
    } | null>(null);
    
    // Ref for the RichTextEditor
    const rteRef = useRef<RichTextEditorComponent>(null);

    const [copiedFullLink, setCopiedFullLink] = useState(false);
    const [copiedPartialLink, setCopiedPartialLink] = useState(false);
    const copyLinkToClipboard = useCallback(
        async (url: string | null, kind: 'full' | 'partial') => {
            if (!url) return;
            try {
                await navigator.clipboard.writeText(url);
                const setter = kind === 'full' ? setCopiedFullLink : setCopiedPartialLink;
                setter(true);
                window.setTimeout(() => setter(false), 1500);
            } catch {
                // navigator.clipboard requires HTTPS / localhost — silent on failure.
            }
        },
        []
    );
    // --- Helper Function to Generate RTE Content ---
    const generateRichText = useCallback((
        currentFirstName: string,
        currentBookingData: typeof bookingDataRef.current,
        currentFullLink: string | null,
        currentPartialLink: string | null,
        currentDepositPercent: number,
        currentReservationState: ReservationState, // Added reservationState
        currentProvider: 'Omise' | 'Stripe' | '2C2P'
    ): ReactNode | null => {
        if (!currentBookingData || !currentFullLink) return null;
        const { totalPrice, totalPriceText, earliestDate, programsEle, roomsEle } = currentBookingData;
        // Calculate total paid and remainder due using standard properties
        // Calculate totals using longs
        const payableLong = multiplyAndRemoveDecimal(currentReservationState.AmountPayableTotal.value);
        const dueLong = multiplyAndRemoveDecimal(currentReservationState.AmountDueTotal.value);
        const totalPaid = payableLong - dueLong; // Now subtracting longs
        const remainderDue = dueLong; // This is already a long after multiplyAndRemoveDecimal
        if (!earliestDate) return null;

        let fullPaymentDueBy = startOfDay(addWeeks(earliestDate, -4));
        let lessThanDueDate = isBefore(fullPaymentDueBy, new Date());
        if (lessThanDueDate) { fullPaymentDueBy = earliestDate; }
        const fullPaymentDueByText = format(fullPaymentDueBy, 'MMMM d, yyyy');

        // Use original Math.round() for display consistency
        const depositAmount = Math.round(totalPrice * (currentDepositPercent / 100));
        const depositStr = longToMoney(depositAmount);

        // Fee wording is driven by the actual rate — zero rates drop all fee references (see paymentFeeRates).
        const fullFeePercent = getFullPaymentFeePercent(currentProvider);
        const partialFeePercent = getPartialPaymentFeePercent(currentProvider, currentDepositPercent);

        return (
             <>
                 <p>Hi {currentFirstName},</p>
                 <br />
                 <p>Perfect! Let's get you booked.</p>
                 <br /><br />
                 {programsEle}
                 {roomsEle}
                 {/* --- POS Items Start --- */}
                 {currentReservationState.PosItems.value.length > 0 && (
                     <>
                         {currentReservationState.PosItems.value.map((item: ReservationPosItem, index: number) => ( // Added type
                             <React.Fragment key={`pos-${index}`}>
                                <b>{item.Description}</b> x {item.Quantity}
                                <br />
                                <b>Price:</b> ฿{longToMoney(multiplyAndRemoveDecimal(item.Amount * item.Quantity))}
                                <hr />
                             </React.Fragment>
                         ))}
                         {/* Removed separate POS total line */}
                     </>
                 )}
                 {/* --- POS Items End --- */}
                  <p><strong>Total Cost:</strong> ฿{totalPriceText}</p>
                  <hr />
                  <p><strong><u>For Payment</u></strong></p>
                  <br />
                 <p>{fullFeePercent > 0
                     ? `Pay in full to reduce credit card processing fees to ${fullFeePercent}%.`
                     : 'Pay in full.'}</p>
                  <br />
                 <p><a href={currentFullLink} target="_blank" rel="noopener noreferrer">Pay In Full Now Via Credit Card</a></p>
                  <br />
                  <p>&nbsp;</p>
                 {/* Conditionally render partial payment link */}
                 {currentDepositPercent !== 100 && (
                     <>
                         <p>Pay minimum {currentDepositPercent}% deposit{partialFeePercent > 0
                             ? ` (subject to ${partialFeePercent}% credit card processing fees).`
                             : '.'}</p>
                         <br />
                         <p><a href={currentPartialLink} target="_blank" rel="noopener noreferrer">Pay {currentDepositPercent}% Now Via Credit Card</a></p>
                         <br/>
                         <p>&nbsp;</p>
                     </>
                 )}
                 {currentProvider === 'Omise' && (
                    <>
                         <p>Our trusted payment gateway, {currentProvider} is a secure and PCI DSS-certified platform used by thousands of businesses worldwide; <b>*if you find your payment is initially rejected, please contact your bank to authorize the transaction for Inmotion Health & Fitness Services instead of attempting to retry the payment*</b>.</p>
                         <br />
                    </>
                 )}
                 <hr />
                 {currentDepositPercent !== 100 && (
                     <>
                         <p>
                             <strong><u>Deposit Required:</u></strong>
                         </p>
                         <p>
                             <strong>Minimum {currentDepositPercent}% (and remainder {lessThanDueDate ? 'on arrival' : '4 weeks prior to arrival'}), </strong><i>or you can choose to pay in full and save
                                 on total transfer fees and exchange rates.</i>
                         </p>
                         <p>
                             <br />
                             Option 1 - Amount {currentDepositPercent}% = <strong>฿{depositStr} Thai Baht to be received today and the remainder to be received by {fullPaymentDueByText}.</strong>
                             <br /><br />
                             or&nbsp;
                         </p>
                         <p>
                             <br />
                             Option 2 - Amount Full Payment (to save on future fees and exchange rates) = <strong>฿{totalPriceText} Thai Baht to be received.</strong>
                         </p>
                     </>
                 )}
                 {currentDepositPercent === 100 && (
                     <>
                         <p>
                             <strong><u>Payment Due</u></strong>
                         </p>
                         <p>
                             <strong>Amount Full Payment:</strong> ฿{depositStr} Thai Baht to be received.
                         </p>
                     </>
                 )}
                 <p><br /><strong>**Once you have paid please send me the transfer receipt and then I will book you space, once funds arrive I will send your booking confirmation.**</strong></p>
                 <p>&nbsp;</p>
                 <p>Kindly review and understand our booking terms and conditions before proceeding to book <a href="http://phuketfit.com/terms-and-conditions/">http://phuketfit.com/terms-and-conditions/</a> and our disclaimer here: <a href="http://phuketfit.com/disclaimer/">http://phuketfit.com/disclaimer/</a><br /><br />Any problems please let me know.&nbsp;<br />&nbsp;</p>
                 <p>Healthy regards,</p>
                 <p>&nbsp;</p>
                 <p>Liam&nbsp;</p>
             </>
        );
    }, []);

    // --- API Call Helper ---
    const callGenerateLinksApi = useCallback(async (
        requests: { paymentType: PaymentLinkType, depositPercent?: number, feePercent: number }[],
        apiProvider: 'Omise' | 'Stripe' | '2C2P'
    ): Promise<PaymentLinkResultItem[] | null> => {
         if (!bookingDataRef.current) { setErrorMessage("Booking data not calculated yet."); return null; }
         const { totalPrice, reservationNumber } = bookingDataRef.current;
         if (!reservationNumber) { setErrorMessage("Reservation number is missing."); return null; }
         if (totalPrice <= 0) { setErrorMessage("Total price must be positive."); return null; }

         const totalAmountDecimal = totalPrice / 100;

         try {
             const response = await fetch('/api/reservations/generate-payment-links', {
                 method: 'POST', headers: { 'Content-Type': 'application/json' },
                 body: JSON.stringify({
                     reservationNumber: String(reservationNumber),
                     totalAmount: totalAmountDecimal,
                     paymentLinkRequests: requests,
                     provider: apiProvider
                 }),
             });
             if (!response.ok) {
                 const errorText = await response.text();
                 throw new Error(`API Error (${response.status}): ${errorText || 'Failed to generate links'}`);
             }
             const data: GeneratePaymentLinksResponse = await response.json();
             return data.paymentLinkResults;
         } catch (error: any) {
             console.error("Error calling GeneratePaymentLinks API:", error);
             setErrorMessage(error.message || 'An unexpected error occurred.');
             // Let the caller set the status to 'error'
             return null;
         }
     }, []); // No dependencies needed as it reads from ref

    // --- Main Effect for Loading Data and Initial Links ---
    useEffect(() => {
        let isMounted = true;
        setStatus('loading');
        setErrorMessage(null);
        setFirstName(''); // Reset name
        bookingDataRef.current = null; // Reset data

        // 1. Fetch Name
        const person = reservationState.Person.value;
        const namePromise = person ? getDisplayName(person, reservationState) : Promise.resolve("Client");

        namePromise
            .then(name => {
                if (!isMounted) return;
                const nameParts = (name || "").split(" ");
                const currentFirstName = nameParts[0] || "Client";
                setFirstName(currentFirstName); // Set state

                // 2. Calculate Booking Data (Synchronous)
                 let calcTotalPrice = 0;
                 let calcEarliestDate: Date | null = null;
                 let programsEle: ReactNode = null;
                 let roomsEle: ReactNode = null;
                 let calcTotalPriceText = '';

                 try {
                     // --- Calculation Logic ---
                     let totalProgramPrice = 0;
                     const programs = reservationState.ProgramBookings.toArray();
                     const transformedPrograms = {} as Record<string, {
                         programName: string, programDays: number, programStartDate: string,
                         programEndDate: string, programPrice: number, quantity: number
                     }>;
                     for (const program of programs) {
                         const programName = getProgramTypeDisplayName(reservationState, program.ProgramType.value);
                         const programPrice = multiplyAndRemoveDecimal(program.Price.price.value.payableAmount);
                         const programDays = countProgramDaysBetween(program.Start.value, program.End.value);
                         const programStartDate = format(program.Start.value, 'MMMM d, yyyy');
                         const programEndDate = format(program.End.value, 'MMMM d, yyyy');
                         const programStartOfDay = startOfDay(program.Start.value);
                         if (!calcEarliestDate || isBefore(programStartOfDay, calcEarliestDate)) { calcEarliestDate = programStartOfDay; }
                         const groupKey = `${programName}_${programStartDate}_${programEndDate}`;
                         const existing = transformedPrograms[groupKey];
                         if (!existing) { transformedPrograms[groupKey] = { programName, programDays, programStartDate, programEndDate, programPrice, quantity: 1 }; }
                         else { existing.programPrice += programPrice; existing.quantity += 1; }
                         totalProgramPrice += programPrice;
                     }
                     programsEle = Object.entries(transformedPrograms).map(([, p]) => (
                          <React.Fragment key={p.programName + p.programStartDate}>
                              <div><p>
                                  <strong>Program:</strong> {p.quantity > 1 ? `${p.quantity} x ` : ''}{p.programName}<br />
                                  <strong>Duration:</strong> {p.programDays} {p.programDays === 1 ? 'day' : 'days'}<br />
                                  <strong>Program start date: </strong> {p.programStartDate}<br />
                                  <strong>Program end date:</strong> {p.programEndDate}<br />
                                  <strong>Program Cost:</strong> ฿{longToMoney(p.programPrice)}
                              </p></div><hr />
                          </React.Fragment>
                     ));

                     let totalRoomPrice = 0;
                     const rooms = reservationState.RoomBookings.toArray();
                     const transformedRooms = {} as Record<string, {
                         roomName: string, roomNights: number, roomCheckInDate: string,
                         roomCheckOutDate: string, roomPrice: number, quantity: number
                     }>;
                     for (const room of rooms) {
                         const roomName = getRoomTypeDisplayName(reservationState, room.RoomType.value);
                         const roomPrice = multiplyAndRemoveDecimal(room.CalculatedPrice.value.totalPayableAmount);
                         const roomNights = Math.ceil(differenceInHours(room.CheckOut.value, room.CheckIn.value) / 24) | 0;
                         const roomCheckInDate = format(room.CheckIn.value, 'MMMM d, yyyy');
                         const roomCheckOutDate = format(room.CheckOut.value, 'MMMM d, yyyy');
                         const roomStartOfDay = startOfDay(room.CheckIn.value);
                         if (!calcEarliestDate || isBefore(roomStartOfDay, calcEarliestDate)) { calcEarliestDate = roomStartOfDay; }
                         const groupKey = `${roomName}_${roomCheckInDate}_${roomCheckOutDate}`;
                         const existing = transformedRooms[groupKey];
                         if (!existing) { transformedRooms[groupKey] = { roomName, roomNights, roomCheckInDate, roomCheckOutDate, roomPrice, quantity: 1 }; }
                         else { existing.roomPrice += roomPrice; existing.quantity += 1; }
                         totalRoomPrice += roomPrice;
                     }
                     roomsEle = Object.entries(transformedRooms).map(([, r]) => (
                          <React.Fragment key={r.roomName + r.roomCheckInDate}>
                              <div><p>
                                  <strong>Accommodation:</strong> {r.quantity > 1 ? `${r.quantity} x ` : ''}{r.roomName}<br />
                                  <strong>Duration:</strong> {r.roomNights} {r.roomNights === 1 ? 'night' : 'nights'}<br />
                                  <strong>Check-in:</strong> {r.roomCheckInDate}<br />
                                  <strong>Check-out:</strong> {r.roomCheckOutDate}<br />
                                  <strong>Accommodation Cost:</strong> ฿{longToMoney(r.roomPrice)}
                              </p></div><hr />
                          </React.Fragment>
                     ));

                     calcTotalPrice = multiplyAndRemoveDecimal(reservationState.AmountPayableTotal.value);
                     calcTotalPriceText = longToMoney(calcTotalPrice);
                     // --- End Calculation Logic ---

                     // Store data in ref
                     bookingDataRef.current = {
                         totalPrice: calcTotalPrice, totalPriceText: calcTotalPriceText,
                         earliestDate: calcEarliestDate, programsEle: programsEle, roomsEle: roomsEle,
                         reservationNumber: reservationState.ReservationNumber
                     };

                     // Validation after calculation
                     if (!bookingDataRef.current.reservationNumber) throw new Error("Reservation number is missing.");
                     if (bookingDataRef.current.totalPrice <= 0) throw new Error("Total price must be positive.");
                     if (!bookingDataRef.current.earliestDate) throw new Error("Could not determine earliest booking date.");

                     // 3. Fetch Initial Links
                     setStatus('generatingLinks');
                     const fullFee = getFullPaymentFeePercent(provider);
                     const partialFee = getPartialPaymentFeePercent(provider, depositPercent);
                     const initialRequests = [
                         { paymentType: PaymentLinkType.Full, feePercent: fullFee },
                         { paymentType: PaymentLinkType.Partial, depositPercent: depositPercent, feePercent: partialFee }
                     ];
                     callGenerateLinksApi(initialRequests, provider).then(results => {
                         if (!isMounted) return;
                         if (results) {
                             let genFullLink: string | null = null;
                             let genPartialLink: string | null = null;
                             let errors: string[] = [];
                             results.forEach(result => {
                                 if (result.error) { errors.push(result.error); }
                                 else if (result.paymentType === PaymentLinkType.Full) { genFullLink = result.url; }
                                 else if (result.paymentType === PaymentLinkType.Partial) { genPartialLink = result.url; }
                             });

                             if (errors.length > 0 || !genFullLink || !genPartialLink) {
                                 setErrorMessage(`Errors during link generation: ${errors.join('; ')}` || "Failed to generate one or both links.");
                                 setStatus('error');
                             } else {
                                 setFullPaymentLink(genFullLink);
                                 setPartialPaymentLink(genPartialLink);
                                 // Store initial partial link in cache
                                 setPartialLinkCache(prev => ({ ...prev, [provider]: { ...prev[provider], [depositPercent]: genPartialLink } }));
                                 // Pass reservationState and provider to generateRichText
                                 const content = generateRichText(currentFirstName, bookingDataRef.current, genFullLink, genPartialLink, depositPercent, reservationState, provider);
                                 setRichTextContent(content);
                                 setRteKey(prevKey => prevKey + 1); // Force remount AFTER content is set
                                 setStatus('ready');
                             }
                         } else { setStatus('error'); }
                     });

                 } catch (calcError: any) {
                      if (!isMounted) return;
                      console.error("Error during booking data calculation:", calcError);
                      setErrorMessage(calcError.message || "Failed to calculate booking details.");
                      setStatus('error');
                 }
            })
            .catch(error => { // Catch name fetch error
                 if (!isMounted) return;
                 console.error("Error fetching display name:", error);
                 setFirstName("Client");
                 setErrorMessage("Failed to fetch guest name.");
                 setStatus('error');
            });

        return () => { isMounted = false; }; // Cleanup

    }, [reservationState, callGenerateLinksApi, generateRichText, provider]); // Rerun only if reservationState or provider changes


    // --- Function to Generate Partial Link (Checks Cache) ---
    const generatePartialLink = useCallback((newDepositPercent: number) => {
        if (!bookingDataRef.current || !firstName || !fullPaymentLink || status === 'generatingLinks') return;

        // --- Handle 100% deposit case explicitly ---
        if (newDepositPercent === 100) {
            console.log("Setting deposit to 100%, hiding partial link.");
            setPartialPaymentLink(null); // Clear partial link
            setDepositPercent(100);      // Update internal state
            // Pass reservationState and provider to generateRichText
            const content = generateRichText(firstName, bookingDataRef.current, fullPaymentLink, null, 100, reservationState, provider);
            setRichTextContent(content);
            setRteKey(prevKey => prevKey + 1); // Force remount
            setStatus('ready');
            return; // Don't proceed to cache check or API call
        }
        // --- End 100% handling ---

        // Check cache first
        if (partialLinkCache[provider] && partialLinkCache[provider][newDepositPercent]) {
            console.log(`Using cached ${provider} link for ${newDepositPercent}%`);
            setPartialPaymentLink(partialLinkCache[provider][newDepositPercent]);
            // Pass reservationState and provider to generateRichText
            const content = generateRichText(firstName, bookingDataRef.current, fullPaymentLink, partialLinkCache[provider][newDepositPercent], newDepositPercent, reservationState, provider);
            setRichTextContent(content);
            setRteKey(prevKey => prevKey + 1); // Force remount AFTER content is set
            setDepositPercent(newDepositPercent); // Ensure internal state matches the displayed link
            setStatus('ready');
            return;
        }

        // Not in cache, generate via API
        console.log(`Generating new ${provider} link for ${newDepositPercent}%`);
        setStatus('generatingPartialLink');
        const partialFee = getPartialPaymentFeePercent(provider, newDepositPercent);
        const partialRequest = [{ paymentType: PaymentLinkType.Partial, depositPercent: newDepositPercent, feePercent: partialFee }];

        callGenerateLinksApi(partialRequest, provider).then(results => {
             if (results && results.length > 0) {
                const partialResult = results.find(r => r.paymentType === PaymentLinkType.Partial);
                if (partialResult?.error) {
                    setErrorMessage(prev => prev ? `${prev}; Partial Link Update Error: ${partialResult.error}` : `Partial Link Update Error: ${partialResult.error}`);
                    setStatus('error');
                } else if (partialResult?.url) {
                    setPartialPaymentLink(partialResult.url);
                    setPartialLinkCache(prevCache => ({ ...prevCache, [provider]: { ...prevCache[provider], [newDepositPercent]: partialResult.url } })); // Add to cache
                    // Pass reservationState and provider to generateRichText
                    const content = generateRichText(firstName, bookingDataRef.current, fullPaymentLink, partialResult.url, newDepositPercent, reservationState, provider);
                    setRichTextContent(content);
                    setRteKey(prevKey => prevKey + 1); // Force remount AFTER content is set
                    setErrorMessage(null);
                    setStatus('ready');
                } else {
                     setErrorMessage("Partial link generation failed silently after update.");
                     setStatus('error');
                }
             } else {
                  setErrorMessage(prev => prev ? `${prev}; Failed to get partial link update response.` : 'Failed to get partial link update response.');
                  setStatus('error');
             }
        });
    }, [firstName, fullPaymentLink, callGenerateLinksApi, generateRichText, status, partialLinkCache, provider]);


    // --- Handlers for Input Change, Blur, and KeyDown ---
    const handleDepositInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const valueStr = e.target.value;
        setDepositPercentInput(valueStr); // Update display value immediately
        const valueNum = parseFloat(valueStr);
        if (!isNaN(valueNum) && valueNum >= 0 && valueNum <= 100) {
            setIsDepositPercentValid(true);
        } else {
            setIsDepositPercentValid(false);
        }
    };

    const triggerLinkGeneration = () => {
        if (!isDepositPercentValid) return;
        const valueNum = parseFloat(depositPercentInput);
        if (isNaN(valueNum) || valueNum < 0 || valueNum > 100) { setIsDepositPercentValid(false); return; }

        // Update the internal percentage state and trigger generation
        setDepositPercent(valueNum);
        generatePartialLink(valueNum);
    };

    const handleDepositInputBlur = () => {
        triggerLinkGeneration();
    };

    const handleDepositInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            triggerLinkGeneration();
            (e.target as HTMLInputElement).blur();
        }
    };


    // --- Render Logic ---
    if (status === 'loading' || status === 'generatingLinks') {
        return <div className="p-4">Loading booking details and payment links...</div>;
    }

    const showControls = status === 'ready' || status === 'generatingPartialLink' || status === 'error';
    const isUpdatingPartial = status === 'generatingPartialLink';

    return (
        <div className="flex flex-col gap-4">
            {/* Error Display */}
            {status === 'error' && (
                 <div className="p-4 text-red-600 border border-red-600 rounded">Error: {errorMessage || 'An unknown error occurred.'}</div>
            )}

            {/* Provider and Deposit Input Area */}
            {showControls && (
                <div className="flex flex-wrap gap-4 items-start">
                    {/* Provider Selection */}
                    <div>
                        <label className="text-sm font-semibold">Payment Provider</label>
                        <div className="flex items-center gap-4 mt-1">
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" name="provider" value="Stripe" checked={provider === 'Stripe'} onChange={() => setProvider('Stripe')} className="form-check-input" disabled={isUpdatingPartial} />
                                Stripe
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" name="provider" value="Omise" checked={provider === 'Omise'} onChange={() => setProvider('Omise')} className="form-check-input" disabled={isUpdatingPartial} />
                                Omise
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" name="provider" value="2C2P" checked={provider === '2C2P'} onChange={() => setProvider('2C2P')} className="form-check-input" disabled={isUpdatingPartial} />
                                2C2P
                            </label>
                        </div>
                    </div>

                    {/* Deposit Percentage */}
                    <div>
                        <label htmlFor="depositPercentInput" className="text-sm font-semibold">Deposit Percentage</label>
                        <div className="flex items-center gap-2 mt-1">
                            <input
                                id="depositPercentInput" type="number" min="0" max="100"
                                value={depositPercentInput}
                                onChange={handleDepositInputChange}
                                onBlur={handleDepositInputBlur}
                                onKeyDown={handleDepositInputKeyDown}
                                className={`form-control w-24 ${!isDepositPercentValid ? 'border-red-500' : ''} ${isUpdatingPartial ? 'opacity-50 cursor-not-allowed' : ''}`}
                                disabled={isUpdatingPartial}
                            /> %
                            {isUpdatingPartial && <span className="text-blue-600 text-sm ml-2">Updating link...</span>}
                        </div>
                        {!isDepositPercentValid && <div className="text-red-500 text-sm mt-1">Please enter a percentage between 0 and 100.</div>}
                    </div>
                </div>
            )}

            {/* Preview Buttons Area */}
            {showControls && (
                <div className="flex justify-between items-center">
                    <div className="flex gap-2 flex-wrap">
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => fullPaymentLink && window.open(fullPaymentLink, '_blank')}
                            disabled={!fullPaymentLink || isUpdatingPartial}
                            title={!fullPaymentLink ? "Full link not available" : "Open full payment link in new tab"} >
                            Preview Full Link
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => copyLinkToClipboard(fullPaymentLink, 'full')}
                            disabled={!fullPaymentLink || isUpdatingPartial}
                            title={!fullPaymentLink ? "Full link not available" : "Copy full payment link to clipboard"}>
                            {copiedFullLink ? 'Copied' : 'Copy Full Link'}
                        </button>
                        {/* Conditionally render partial preview + copy buttons */}
                        {depositPercent !== 100 && (
                            <>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => partialPaymentLink && window.open(partialPaymentLink, '_blank')}
                                    disabled={!partialPaymentLink || isUpdatingPartial}
                                    title={!partialPaymentLink ? "Partial link not available" : `Open ${depositPercent}% deposit link in new tab`} >
                                    Preview {depositPercent}% Link
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => copyLinkToClipboard(partialPaymentLink, 'partial')}
                                    disabled={!partialPaymentLink || isUpdatingPartial}
                                    title={!partialPaymentLink ? "Partial link not available" : `Copy ${depositPercent}% deposit link to clipboard`}>
                                    {copiedPartialLink ? 'Copied' : `Copy ${depositPercent}% Link`}
                                </button>
                            </>
                        )}
                    </div>
                    <MacroCopyButtons getHtml={() => rteRef.current?.getHtml() ?? null} />
                </div>
            )}

            {/* Rich Text Editor */}
            {(status === 'ready' || isUpdatingPartial) ? (
                <div>
                    {/* Key ensures RTE remounts AFTER content is ready */}
                    <RichTextEditorComponent key={rteKey} ref={rteRef}>
                        {richTextContent}
                        <Inject services={[Toolbar, Image, Link, HtmlEditor, QuickToolbar]} />
                    </RichTextEditorComponent>
                </div>
            ) : (
                 !errorMessage && <div className="p-4">Generating content...</div>
            )}
        </div>
    );
}

// --- Renamed Props Interface ---
export interface LetsGetYouBookedMacroPaymentLinkComponentProps {
    reservationState: ReservationState;
}